import { Component, Input, OnChanges } from '@angular/core';

import { ChartPointModel }           from '../typings/intelligence.chart-point';
import { ChartSeriesModel }          from '../typings/intelligence.chart-series';
import { ChartTooltipModel }         from '../typings/intelligence.chart-tooltip';
import { IntelligenceFormatService } from '../intelligence.format.service';

interface RenderedAreaSeries {
  areaPath: string;
  color   : string;
  linePath: string;
}

@Component({
  selector   : 'wor-intelligence-area-chart',
  template: require('./intelligence.area-chart.component.html')
})
export class AreaChartComponent implements OnChanges {
  @Input() format = 'money';
  @Input() points : Array<ChartPointModel> = [];
  @Input() series : Array<ChartSeriesModel> = [];
  @Input() stacked = false;
  @Input() width = 640;

  height = 260;
  hoverDots : Array<{ color : string; x : number; y : number; }> = [];
  hoverIndex = -1;
  hoverX = -1;
  padding = { bottom: 26, left: 54, right: 14, top: 12 };
  rendered : Array<RenderedAreaSeries> = [];
  ticks : Array<{ label : string; y : number; }> = [];
  tooltip : ChartTooltipModel = { rows: [], title: '', visible: false, x: 0, y: 0 };
  xLabels : Array<{ text : string; x : number; }> = [];

  private yMax = 1;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService
  ) {}

  ngOnChanges () : void {
    this.render();
  }

  formatValue ( value : number ) : string {
    return this.format === 'count'
      ? this.intelligenceFormatService.count(value)
      : this.intelligenceFormatService.money(value);
  }

  hideTooltip () : void {
    this.hoverDots = [];
    this.hoverIndex = -1;
    this.hoverX = -1;
    this.tooltip = { ...this.tooltip, visible: false };
  }

  onMove ( event : MouseEvent ) : void {
    if ( !this.points.length ) {
      return;
    }

    const svg = event.currentTarget as SVGSVGElement;
    const bounds = svg.getBoundingClientRect();
    const plotX = ( event.clientX - bounds.left ) * ( this.width / bounds.width );

    this.hoverIndex = this.nearestIndex(plotX);
    this.hoverX = this.pointX(this.hoverIndex);

    const point = this.points[ this.hoverIndex ];
    const stacks = this.stackedValues(this.hoverIndex);

    this.hoverDots = this.series.map(( item, index ) => ({
      color: item.color,
      x    : this.hoverX,
      y    : this.pointY(
        this.stacked
          ? stacks[ index ]
          : ( point.values[ item.key ] || 0 )
      )
    }));

    this.tooltip = {
      rows: this.series.map(item => ({
        color: item.color,
        label: item.label,
        value: this.formatValue(point.values[ item.key ] || 0)
      })),
      title  : point.label,
      visible: true,
      x      : Math.min(event.clientX - bounds.left + 14, bounds.width - 170),
      y      : Math.max(event.clientY - bounds.top - 10, 4)
    };
  }

  private nearestIndex ( plotX : number ) : number {
    const count = this.points.length;

    if ( count < 2 ) {
      return 0;
    }

    const step = ( this.width - this.padding.left - this.padding.right ) / ( count - 1 );
    const index = Math.round(( plotX - this.padding.left ) / step);

    return Math.min(Math.max(index, 0), count - 1);
  }

  private niceCeil ( value : number ) : number {
    if ( value <= 0 ) {
      return 1;
    }

    const power = Math.pow(10, Math.floor(Math.log(value) / Math.LN10));
    const scaled = value / power;
    const nice = scaled <= 1
      ? 1
      : scaled <= 2
        ? 2
        : scaled <= 2.5
          ? 2.5
          : scaled <= 5
            ? 5
            : 10;

    return nice * power;
  }

  private pointX ( index : number ) : number {
    const innerWidth = this.width - this.padding.left - this.padding.right;
    const count = this.points.length;

    return count < 2
      ? this.padding.left + innerWidth / 2
      : this.padding.left + ( index * innerWidth ) / ( count - 1 );
  }

  private pointY ( value : number ) : number {
    const innerHeight = this.height - this.padding.top - this.padding.bottom;

    return this.padding.top + innerHeight - ( Math.max(value, 0) / this.yMax ) * innerHeight;
  }

  private render () : void {
    this.hideTooltip();
    this.rendered = [];
    this.ticks = [];
    this.xLabels = [];

    if ( !this.points.length || !this.series.length ) {
      return;
    }

    let max = 0;

    this.points.forEach(( point, index ) => {
      if ( this.stacked ) {
        const stacks = this.stackedValues(index);

        max = Math.max(max, stacks[ stacks.length - 1 ] || 0);
      } else {
        this.series.forEach(item => max = Math.max(max, point.values[ item.key ] || 0));
      }
    });

    this.yMax = this.niceCeil(max);

    const baseY = this.pointY(0);

    this.series.forEach(( item, seriesIndex ) => {
      const upper = this.points.map(( point, index ) => {
        const value = this.stacked
          ? this.stackedValues(index)[ seriesIndex ]
          : ( point.values[ item.key ] || 0 );

        return { x: this.pointX(index), y: this.pointY(value) };
      });

      const lower = this.points.map(( point, index ) => {
        const value = this.stacked && seriesIndex > 0
          ? this.stackedValues(index)[ seriesIndex - 1 ]
          : 0;

        return { x: this.pointX(index), y: value > 0 ? this.pointY(value) : baseY };
      });

      const linePath = upper
        .map(( coordinate, index ) => `${ index === 0 ? 'M' : 'L' }${ coordinate.x } ${ coordinate.y }`)
        .join(' ');

      const areaPath = `${ linePath } ${ lower
        .slice()
        .reverse()
        .map(coordinate => `L${ coordinate.x } ${ coordinate.y }`)
        .join(' ') } Z`;

      this.rendered.push({ areaPath, color: item.color, linePath });
    });

    for ( let index = 0; index <= 4; index++ ) {
      const value = ( this.yMax / 4 ) * index;

      this.ticks.push({
        label: this.format === 'count'
          ? this.intelligenceFormatService.count(value)
          : this.intelligenceFormatService.compactMoney(value),
        y: this.pointY(value)
      });
    }

    const labelIndexes = this.points.length < 3
      ? this.points.map(( _point, index ) => index)
      : [ 0, Math.floor(( this.points.length - 1 ) / 2), this.points.length - 1 ];

    labelIndexes.forEach(index => {
      this.xLabels.push({
        text: this.points[ index ].label,
        x   : this.pointX(index)
      });
    });
  }

  private stackedValues ( index : number ) : Array<number> {
    const point = this.points[ index ];
    let running = 0;

    return this.series.map(item => {
      running += point.values[ item.key ] || 0;

      return running;
    });
  }
}
